'use client'

import * as React from 'react'

import 'react-quill/dist/quill.snow.css'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'
import { useHashtagClickHandler } from '@/hooks/use-hashtag-click-handler'

/**
 * Generic prop helper:
 *  - `C` is the element or component you want to render
 *  - merge its own props with our custom ones,
 *    while hiding children / dangerouslySetInnerHTML so we control them
 */
type PolymorphicProps<C extends React.ElementType> = {
  content: string
  className?: string
  as?: C
  onHashtagClick?: (hashtag: string) => void
} & Omit<React.ComponentPropsWithoutRef<C>, 'children' | 'dangerouslySetInnerHTML' | 'ref'>

/**
 * Renders HTML with clickable #hashtags.
 */
export function ClickableHashtagContent<C extends React.ElementType = 'div'>(props: PolymorphicProps<C>) {
  const { content, className, as, onHashtagClick, ...rest } = props

  const Component = (as || 'div') as React.ElementType

  const contentRef = React.useRef<HTMLElement | null>(null)
  const sanitizedContent = React.useMemo(() => sanitizeHtmlValue(content || ''), [content])

  useHashtagClickHandler(contentRef, onHashtagClick)

  React.useEffect(() => {
    if (!contentRef.current) return

    const highlightCodeBlocks = async () => {
      const codeBlocks = contentRef.current?.querySelectorAll('pre.hljs[data-language]')
      if (!codeBlocks || codeBlocks.length === 0) return

      const hljs = await import('highlight.js/lib/core')

      const [javascript, typescript, python, java, cpp, csharp, html, css, json, bash] = await Promise.all([
        import('highlight.js/lib/languages/javascript'),
        import('highlight.js/lib/languages/typescript'),
        import('highlight.js/lib/languages/python'),
        import('highlight.js/lib/languages/java'),
        import('highlight.js/lib/languages/cpp'),
        import('highlight.js/lib/languages/csharp'),
        import('highlight.js/lib/languages/xml'), // for HTML
        import('highlight.js/lib/languages/css'),
        import('highlight.js/lib/languages/json'),
        import('highlight.js/lib/languages/bash'),
      ])

      hljs.default.registerLanguage('javascript', javascript.default)
      hljs.default.registerLanguage('typescript', typescript.default)
      hljs.default.registerLanguage('python', python.default)
      hljs.default.registerLanguage('java', java.default)
      hljs.default.registerLanguage('cpp', cpp.default)
      hljs.default.registerLanguage('csharp', csharp.default)
      hljs.default.registerLanguage('html', html.default)
      hljs.default.registerLanguage('xml', html.default)
      hljs.default.registerLanguage('css', css.default)
      hljs.default.registerLanguage('json', json.default)
      hljs.default.registerLanguage('bash', bash.default)

      codeBlocks.forEach(block => {
        const pre = block as HTMLPreElement
        const hasHighlighting = pre.querySelector('.hljs-keyword, .hljs-string, .hljs-comment')
        if (hasHighlighting) return

        const language = pre.getAttribute('data-language') || 'plaintext'
        const code = pre.textContent || ''

        try {
          const highlighted = hljs.default.highlight(code, { language, ignoreIllegals: true })
          pre.innerHTML = highlighted.value
        } catch (error) {
          try {
            const highlighted = hljs.default.highlightAuto(code)
            pre.innerHTML = highlighted.value
          } catch (autoError) {
            // eslint-disable-next-line no-console
            console.warn('Failed to highlight code block:', autoError)
          }
        }
      })
    }

    highlightCodeBlocks()
  }, [sanitizedContent])

  React.useEffect(() => {
    const handleMentionClick = (e: Event) => {
      const target = e.target as HTMLElement
      const mentionElement = target.closest('a.mention, .mention, .ql-mention') as HTMLElement | null
      if (!mentionElement) return

      if (mentionElement instanceof HTMLAnchorElement) {
        const href = mentionElement.getAttribute('href')
        if (href?.startsWith('/user/')) {
          e.stopPropagation()
          return
        }
      }

      const profileId =
        mentionElement.getAttribute('data-profile-id') || mentionElement.getAttribute('data-mention-id')
      if (profileId) {
        e.preventDefault()
        e.stopPropagation()
        window.location.assign(`/user/${profileId}`)
      }
    }

    document.addEventListener('click', handleMentionClick, true)

    return () => {
      document.removeEventListener('click', handleMentionClick, true)
    }
  }, [])

  return (
    <>
      <Component
        /* callback ref is valid for every element type */
        ref={(node: HTMLElement | null) => {
          contentRef.current = node as HTMLElement | null
        }}
        className={cn('clickable-hashtag-content ql-editor', className)}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        {...rest}
      />

      <style jsx global>{`
        .ql-container.ql-snow {
          border: none !important;
        }

        .ql-editor {
          border: none !important;
          padding: 12px 0 !important;
          font-size: 16px !important;
          line-height: 1.3 !important;
          color: #1c1e21 !important;
          min-height: auto !important;
          /* Ensure proper text flow */
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        .clickable-hashtag-content .code-block {
          /* Restore full code block styling for display */
          display: block !important;
          background-color: #282c34 !important;
          border: 1px solid #444 !important;
          border-radius: 8px !important;
          overflow-x: auto !important;
          cursor: default !important;
        }

        .clickable-hashtag-content .code-block pre {
          margin: 0 !important;
          padding: 0 !important;
          font-family: Monaco, Menlo, 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          color: #abb2bf !important;
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
          background: transparent !important;
        }

        .clickable-hashtag-content .code-block .hljs {
          background: transparent !important;
          color: #abb2bf !important;
        }

        /* Syntax highlighting for code blocks */
        .clickable-hashtag-content .code-block .hljs-keyword,
        .clickable-hashtag-content .code-block .hljs-selector-tag,
        .clickable-hashtag-content .code-block .hljs-built_in,
        .clickable-hashtag-content .code-block .hljs-name {
          color: #c678dd !important;
        }

        .clickable-hashtag-content .code-block .hljs-string,
        .clickable-hashtag-content .code-block .hljs-attr {
          color: #98c379 !important;
        }

        .clickable-hashtag-content .code-block .hljs-comment,
        .clickable-hashtag-content .code-block .hljs-quote {
          color: #5c6370 !important;
          font-style: italic !important;
        }

        .clickable-hashtag-content .code-block .hljs-number,
        .clickable-hashtag-content .code-block .hljs-literal {
          color: #d19a66 !important;
        }

        .clickable-hashtag-content .code-block .hljs-function,
        .clickable-hashtag-content .code-block .hljs-title {
          color: #61afef !important;
        }

        .clickable-hashtag-content .code-block .hljs-variable,
        .clickable-hashtag-content .code-block .hljs-params {
          color: #e06c75 !important;
        }

        .clickable-hashtag-content .code-block .hljs-type,
        .clickable-hashtag-content .code-block .hljs-class {
          color: #e5c07b !important;
        }

        .clickable-hashtag-content .code-block .hljs-operator {
          color: #56b6c2 !important;
        }

        .clickable-hashtag-content .code-block .hljs-property {
          color: #d19a66 !important;
        }

        .clickable-hashtag-content .ql-hashtag {
          display: inline-block !important;
          line-height: 1 !important;
          padding: 2px 4px !important;
          border-radius: 4px !important;
          color: #1d4ed8 !important;
          background-color: #e0f2fe !important;
          text-decoration: none !important;
          cursor: pointer;
        }
        .clickable-hashtag-content .ql-hashtag:hover {
          background-color: #bae6fd !important;
        }
        .clickable-hashtag-content .ql-hashtag:active {
          background-color: #7dd3fc !important;
        }

        .clickable-hashtag-content .mention,
        .clickable-hashtag-content .ql-mention {
          display: inline !important;
          font-weight: 700 !important;
          color: #1d4ed8 !important;
          background: transparent !important;
          text-decoration: none !important;
          cursor: pointer !important;
          margin: 0 !important;
        }

        .clickable-hashtag-content .mention:hover,
        .clickable-hashtag-content .ql-mention:hover {
          color: #1d4ed8 !important;
          text-decoration: underline !important;
        }

        .clickable-hashtag-content .custom-emoji-inline {
          display: inline-block !important;
          width: 24px !important;
          height: 24px !important;
          line-height: 1 !important;
          vertical-align: text-bottom !important; /* or 'middle' if you prefer */
          margin: 0 2px !important;
          border-radius: 4px !important;
          object-fit: cover !important;
          pointer-events: none; /* optional: so clicking doesn't move the caret oddly */
        }
        .clickable-hashtag-content blockquote {
          border-left: 4px solid #65676b !important;
          padding-left: 12px !important;
          margin: 12px 0 !important;
          font-style: italic !important;
          font-size: 16px !important;
          line-height: 1.5 !important;
          color: #65676b !important;
          background-color: #f8f9fa !important;
          border-radius: 4px !important;
          padding: 12px !important;
        }

        .clickable-hashtag-content .clickable-image-wrapper,
        .clickable-hashtag-content .clickable-image-wrapper img {
          max-width: min(100%, 220px) !important;
        }

        .clickable-hashtag-content img:not(.custom-emoji-inline) {
          max-width: min(100%, 220px) !important;
          max-height: 160px !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain !important;
        }
      `}</style>
    </>
  )
}
